import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, createId, IconButton, Text } from '@bloomberg/media-ui-core';
import { ExitThin } from '@bloomberg/media-ui-icons';
import type { Placement } from '@floating-ui/react';
import {
  autoUpdate,
  FloatingPortal,
  hide,
  offset,
  useFloating,
} from '@floating-ui/react';

import styles from './styles.module.css';

const SMALL_SCREEN_BREAKPOINT = 620;
const MEDIUM_SCREEN_BREAKPOINT = 768;

export type OverlayProps = GlobalProps & {
  headerText: string;
  onExit: () => void;
  placement?: Placement;
  Icon?: React.ReactNode;
  anchorElementRef?:
    | React.RefObject<HTMLElement>
    | React.MutableRefObject<HTMLElement>;
  children?: React.ReactNode;
  usePortal?: boolean;
};

export const Overlay = React.forwardRef<HTMLDivElement, OverlayProps>(
  function Overlay(
    props: OverlayProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) {
    const {
      headerText,
      onExit,
      children,
      className,
      Icon,
      anchorElementRef,
      placement,
      usePortal,
    } = props;

    const headerClasses = [styles.headerWrapper, Icon && styles.withIcon];

    const mobileAnchorRef = useRef<HTMLDivElement>(null);

    const [overlayId] = useState(`overlay-${createId()}`);
    const [currentAnchor, setCurrentAnchor] = useState<HTMLElement | null>();
    const [currentPlacement, setCurrentPlacement] = useState<Placement>();
    const [isHidden, setIsHidden] = useState<boolean>(false);
    const [width, setWidth] = useState<number>(window?.innerWidth || 0);

    const onExitOverlay = useCallback(() => {
      if (window.innerWidth < MEDIUM_SCREEN_BREAKPOINT) {
        // Slide down animation for mobile overlay
        setCurrentPlacement('bottom-start');
        setTimeout(() => {
          onExit();
        }, 150);
      } else {
        onExit();
      }
    }, [setCurrentPlacement, onExit]);

    const onMaybeClickOutside = useCallback(
      (e: Event) => {
        const clickedOutsideOfOverlay = !document
          .getElementById(overlayId)
          ?.contains(e.target as Node | null);
        const clickedOutsideOfAnchor = !anchorElementRef?.current?.contains(
          e.target as Node | null
        );

        if (clickedOutsideOfAnchor && clickedOutsideOfOverlay) {
          onExitOverlay();
        }
      },
      [onExitOverlay, overlayId, anchorElementRef]
    );

    const resetAnchorAndPlacement = useCallback(() => {
      if (window.innerWidth < SMALL_SCREEN_BREAKPOINT) {
        setCurrentAnchor(mobileAnchorRef?.current);

        // Slide up animation for mobile overlay
        setCurrentPlacement('bottom-start');
        setIsHidden(true);
        setTimeout(() => {
          setCurrentPlacement('top-start');
          setIsHidden(false);
        }, 50);
      } else {
        setCurrentAnchor(anchorElementRef?.current);
        setCurrentPlacement(placement);
      }
    }, [
      mobileAnchorRef,
      anchorElementRef,
      setCurrentAnchor,
      setCurrentPlacement,
    ]);

    useEffect(() => {
      window.addEventListener('resize', resetAnchorAndPlacement);

      return () =>
        window.removeEventListener('resize', resetAnchorAndPlacement);
    }, [mobileAnchorRef, anchorElementRef]);

    useEffect(resetAnchorAndPlacement, []);

    useEffect(() => {
      window.addEventListener('click', onMaybeClickOutside);

      return () => window.removeEventListener('click', onMaybeClickOutside);
    }, [onMaybeClickOutside]);

    useLayoutEffect(() => {
      setWidth(window.innerWidth);
    }, [window.innerWidth]);

    const { refs, floatingStyles, isPositioned } = useFloating({
      elements: {
        reference: currentAnchor,
      },
      open: true,
      placement: currentPlacement,
      middleware: [
        width >= SMALL_SCREEN_BREAKPOINT && offset(2),
        hide({ strategy: 'referenceHidden', padding: 10 }),
        hide({ strategy: 'escaped', padding: 10 }),
      ],
      whileElementsMounted: autoUpdate,
    });

    const overlayContent = (
      <>
        <div className={styles.mobileOverlayWrapper} onClick={onExitOverlay} />
        <div
          id={overlayId}
          ref={refs.setFloating}
          className={classes([
            className,
            styles.wrapper,
            (isHidden || !isPositioned) && styles.hiddenWrapper,
          ])}
          style={floatingStyles}
        >
          <div ref={ref} className={classes(headerClasses)}>
            {Icon && <div className={styles.headerIcon}>{Icon}</div>}
            <Text className={styles.header} as="h6">
              {headerText}
            </Text>
            <IconButton
              className={styles.dismissOverlay}
              onClick={onExitOverlay}
            >
              <ExitThin />
            </IconButton>
          </div>
          <div className={styles.body}>{children}</div>
        </div>
      </>
    );

    return (
      <RemoveScroll enabled={width < SMALL_SCREEN_BREAKPOINT}>
        <div ref={mobileAnchorRef} className={styles.mobileAnchor} />
        {usePortal ? (
          <FloatingPortal>{overlayContent}</FloatingPortal>
        ) : (
          overlayContent
        )}
      </RemoveScroll>
    );
  }
);
